// models/commerce/OrderBox.js - PRODUCTION READY
import { useAxiosRepo } from '@pinia-orm/axios';
import { Model, useRepo } from 'pinia-orm';
import { DateCast } from 'pinia-orm/casts';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { ItemStatusCategories } from '@/enums/commerce/ItemStatusConfig.js';
import { OrderBoxStatus, OrderBoxStatusMeta } from '@/enums/commerce/OrderBoxStatus.enum.js';
import ClubMembership from '@/models/ClubMembership.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import Summary from '@/models/commerce/Summary.js';


/**
 * OrderBox Model
 *
 * RESPONSIBILITIES:
 * - Schema definition (normalized state structure)
 * - Relationships (items, membership)
 * - Business logic (status checks, calculations)
 * - Query scopes (static methods for common queries)
 * - API integration (via Pinia ORM Axios plugin)
 */
export default class OrderBox extends Model {
  // ==========================================================================
  // SCHEMA DEFINITION
  // ==========================================================================

  static entity = 'order-boxes';

  static primaryKey = 'id';

  static fields() {
    return {
      id: this.uid(),
      code: this.attr(''),
      title: this.attr(''),
      status: this.attr(OrderBoxStatus.DRAFT),
      shipName: this.attr(''),
      shipPhone: this.attr(''),
      shipAddress: this.attr(''),
      shipGovernorate: this.attr(''),
      memberId: this.attr(''),
      totalItems: this.attr(0),
      totalAmount: this.attr(0), // net box total (Σ line amount); Poola carton = cash paid
      isPoola: this.attr(false), // dedicated pre-paid Poola carton (title-keyed SSOT)
      // MUST default to null, never ''. Both are cast with DateCast below, and
      // pinia-orm's DateCast.set() early-returns on null but runs
      // `new Date(Date.parse(value)).toISOString()` on any string — so ''
      // becomes NaN and throws `RangeError: Invalid time value`. pinia-orm
      // builds stub instances with field defaults while normalizing relations,
      // so a '' default crashed hydration (and the whole page) even when the
      // API sent perfectly valid dates. See the 2026-08-10 browser-pass audit
      // (F2): it broke /orders/box/:id/totals/ for every box.
      createdAt: this.attr(null),
      updatedAt: this.attr(null),

      // Stage-aware cost projection — present on EVERY response from the
      // collection and single-box endpoints, including DRAFT. Shape:
      //   { stage, subtotalCents, commissionCents, commissionRateBp,
      //     adjustmentsTotalCents, totalCents, lineCount, isEstimated, isFinal }
      // `null` only for malformed payloads — treat as "no totals yet" in the UI.
      estimatedTotal: this.attr(null),

      // Invoice-document fields — minted on PACKED → SHIPPED transition, only
      // populated by the dedicated /order-boxes/{id}/invoice endpoint.
      // invoiceVersion is the optimistic-concurrency token POST /confirm-delivery
      // must echo (409 on mismatch).
      invoiceNumber: this.attr(null),
      invoiceVersion: this.attr(null),

      // The persisted invoice columns, written at SHIP and re-applied verbatim
      // on every rebuild. MUST be declared or pinia-orm drops them on save.
      //
      // Read these — NOT `estimatedTotal` — whenever a box carries an
      // invoiceNumber. `estimatedTotal` only reads the same columns in its
      // 'shipped' and 'final' stages; a DELIVERED-but-not-yet-confirmed box
      // falls back to the live draft projection, which excludes Poola lines
      // from the commission base while still summing their adjustments. On a
      // pre-paid Poola carton that projects commission 0 against a −92.70
      // waiver and a NEGATIVE total. These columns never lie.
      commissionCents: this.attr(null),
      commissionRateBp: this.attr(null),
      totalInvoiceCents: this.attr(null),
      shippedAt: this.attr(null),
      deliveredAt: this.attr(null),
      deliveredConfirmedAt: this.attr(null),
      deliveredConfirmedBy: this.attr(null),
      deliveredConfirmationMethod: this.attr(null),

      // Per-line adjustment entries on the invoice. Each entry shape:
      //   { id, label, amount_cents, is_commissionable, type, added_by, added_at }
      // `type` may be 'manual' (admin-entered) or 'rounding_down' (system entry
      // that snaps the pre-round total down to the nearest pound — surfaced
      // separately in the breakdown so the math is verifiable to the member).
      // `adjustmentsTotalCents` on estimatedTotal already includes these.
      adjustmentsJson: this.attr([]),

      // Sum of poolshare yield (in cents) across all in-flight items in this
      // box. Pre-DELIVERED this is the *potential* reward the member will
      // unlock by confirming delivery; post-DELIVERED/CLOSED it equals the
      // amount that was actually released into the PoolShare wallet. Null/0
      // when no poolshare-eligible items are in the box (UI hides the row).
      potentialPoolShareCents: this.attr(0),

      // The carton account — the invoice PDF's three money blocks, items by shop and
      // mini statement, as one object (brief 2026-09-29-carton-account; the shape is
      // ../backend/app/DataObjects/Commerce/CartonAccount/CartonAccount.php). Always on
      // GET /order-boxes/{id}/invoice; null before the carton ships and for the July
      // Poola cartons. MUST be declared or pinia-orm drops it on save.
      cartonAccount: this.attr(null),

      // «خلصت طلباتي» and the packaging team's review (done-ordering pack, brief
      // 2026-10-17-done-ordering-review). ONE fact on the carton, no new status: it
      // stays ACTIVE. All read-only on the wire and MUST be declared or pinia-orm
      // drops them on save. Dates stay plain ISO strings (no DateCast) — they are
      // only ever tested for "set or not" and shown through the date formatters.
      //   doneOrderingAt  — she said she is done ordering (null = still ordering)
      //   reviewSentAt    — the team sent her the review (null = not yet)
      //   reviewComplaint — her carton-level complaint on that review (null = none)
      //   reviewMedia     — the team's photos/clip, in attach order:
      //                     [{ id, type: 'image'|'video', url, thumbnailUrl|null }]
      doneOrderingAt: this.attr(null),
      reviewSentAt: this.attr(null),
      reviewComplaint: this.attr(null),
      reviewMedia: this.attr([]),

      // Relationships - Pinia ORM handles normalization
      items: this.hasMany(OrderBoxItem, 'orderBoxId'),
      membership: this.belongsTo(ClubMembership, 'memberId'),
      summaries: this.hasMany(Summary, 'orderBoxId'),
    };
  }
  static casts() {
    return {
      createdAt: DateCast,
      updatedAt: DateCast,
    };
  }

  static saving(model) {
    console.log('saving', model);
    if (model.summaries?.length) {
      // useRepo(Summary).save(model.summaries);
    }
  }

  // ==========================================================================
  // QUERY SCOPES - Replace composable computed values
  // ==========================================================================

  /**
     * Get all active boxes (not delivered/cancelled/closed).
     * Usage: OrderBox.active().get()
     */
  static active() {
    return useRepo(this).query().whereIn('status', [
      OrderBoxStatus.DRAFT,
      OrderBoxStatus.ACTIVE,
      OrderBoxStatus.READY,
      OrderBoxStatus.PACKED,
      OrderBoxStatus.SHIPPED,
    ]);
  }

  /**
     * Get boxes that can accept new items.
     * Usage: OrderBox.available().get()
     */
  static available() {
    return useRepo(this).query().whereIn('status', [
      OrderBoxStatus.DRAFT,
      OrderBoxStatus.ACTIVE,
    ]);
  }

  /**
     * Get closed boxes.
     * Usage: OrderBox.closed().get()
     */
  static closed() {
    return useRepo(this).query().whereIn('status', [
      OrderBoxStatus.DELIVERED,
      OrderBoxStatus.CANCELLED,
      OrderBoxStatus.CLOSED,
    ]);
  }

  /**
     * Find box with items loaded. Only load 'items' explicitly, not
     * 'withAllRecursive' — recursion causes major slowdowns.
     *
     * Usage: OrderBox.withItems(boxId)
     */
  static withItems(id) {
    return useRepo(this).query().with('items').find(id);
  }

  // ==========================================================================
  // BUSINESS LOGIC - Instance Getters
  // ==========================================================================

  /**
     * Check if box is editable
     */
  get isEditable() {
    return [OrderBoxStatus.DRAFT, OrderBoxStatus.ACTIVE].includes(this.status);
  }

  /**
     * Check if box can accept new items
     */
  get canAddItems() {
    return this.isEditable && this.status !== OrderBoxStatus.CANCELLED;
  }

  /**
     * Check if box can request shipping
     */
  get canRequestShipping() {
    return this.status === OrderBoxStatus.ACTIVE && this.issuesCount === 0;
  }

  /**
     * Get active (non-cancelled) items
     */
  get activeItems() {
    if (!this.items) return [];
    return this.items.filter(item => item.status !== ItemStatus.CANCELLED);
  }

  /**
     * Get count of items blocking shipping. OUT_OF_STOCK is deliberately
     * excluded — it's terminal awareness, not a ship-blocking problem (see
     * ItemStatusCategories.BLOCKS_SHIPPING, the SSOT shared with PackageCard's
     * own issues banner).
     */
  get issuesCount() {
    if (!this.items) return 0;
    return this.items.filter(item =>
      ItemStatusCategories.BLOCKS_SHIPPING.includes(item.status),
    ).length;
  }

  /**
     * Get count of items in progress
     */
  get progressCount() {
    if (!this.items) return 0;
    return this.items.filter(item =>
      [ItemStatus.PENDING, ItemStatus.COLLECTING].includes(item.status),
    ).length;
  }

  /**
     * Get count of completed items
     */
  get doneCount() {
    if (!this.items) return 0;
    return this.items.filter(item =>
      [ItemStatus.COLLECTED, ItemStatus.PACKAGED].includes(item.status),
    ).length;
  }

  /**
     * Get unique channels from items
     */
  get channels() {
    if (!this.items) return [];
    const channels = new Set(
      this.activeItems.map(item => item.channelName),
    );
    return Array.from(channels).sort();
  }

  /**
     * Check if shipping info is complete
     */
  get hasCompleteShippingInfo() {
    return !!(
      this.shipName?.trim() &&
            this.shipPhone?.trim() &&
            this.shipAddress?.trim() &&
            this.shipGovernorate?.trim()
    );
  }

  /**
     * Format total amount as currency
     */
  get formattedTotal() {
    return `${(this.totalAmount / 100).toFixed(2)} EGP`;
  }

  // ==========================================================================
  // API METHODS - Using Pinia ORM Axios Plugin
  // ==========================================================================

  /**
     * Fetch all boxes for membership
     * Auto-persists to store with { save: true }
     *
     * @param {string} membershipId
     * @returns {Promise<OrderBox[]>}
     */
  static async fetchAll(membershipId) {
    await useAxiosRepo(this).api().get(
      `/club-memberships/${membershipId}/order-boxes`,
      {
        params: { include: 'items' },
        save: true, // Auto-persist to Pinia ORM store
      },
    );

    return this.all();
  }

  /**
     * Fetch single box with items
     *
     * @param {string} id
     * @returns {Promise<OrderBox>}
     */
  static async fetchOne(id) {
    await useAxiosRepo(this).api().get(
      `/order-boxes/${id}`,
      {
        params: { include: 'items' },
        save: true,
      },
    );

    return this.withItems(id);
  }

  /**
     * Create new box
     *
     * @param {Object} data - Box data
     * @returns {Promise<OrderBox>}
     */
  static async create(data) {
    const response = await useAxiosRepo(this).api().post(
      '/order-boxes',
      {
        data: {
          type: 'order-boxes',
          attributes: data,
        },
      },
      { save: true },
    );

    const boxId = response.response.data.data.id;
    return this.withItems(boxId);
  }

  /**
     * Update box (instance method)
     *
     * @param {Object} updates
     * @returns {Promise<OrderBox>}
     */
  async update(updates) {
    await useAxiosRepo(OrderBox).api().patch(
      `/order-boxes/${this.id}`,
      {
        data: {
          type: 'order-boxes',
          id: this.id,
          attributes: updates,
        },
      },
      { save: true },
    );

    return OrderBox.withItems(this.id);
  }

  /**
     * Request shipping (change status to Ready)
     *
     * @returns {Promise<OrderBox>}
     */
  async requestShipping() {
    await useAxiosRepo(OrderBox).api().post(
      `/order-boxes/${this.id}/request-shipping`,
      {},
      { save: true },
    );

    return OrderBox.withItems(this.id);
  }

  /**
     * Take her own carton back out of "waiting to ship" (READY → ACTIVE).
     *
     * BE→FE brief `2026-09-15-ask-to-ship-notice-and-member-reopen.md`, owner
     * answer H-I. `POST /order-boxes/{id}/reopen`, no body, returns the box —
     * exactly like `requestShipping()` above it.
     *
     * ── WHY IT RE-READS THE ITEMS AND `requestShipping` ALSO HAS TO ────────
     * This is the same service verb as the admin's Re-open button, so the
     * reversal is total: every line the ship request lifted to PACKAGED drops
     * back to COLLECTED, `ready_at` clears and the box totals recompute. The
     * LINE statuses change, so a status-only refresh of the box would leave
     * every row on this device claiming to be packaged. `withItems` is not an
     * optimisation here; it is the correctness.
     *
     * TWO REFUSALS, AND THE DIFFERENCE MATTERS TO THE CALLER. A 403 is "not
     * her carton" and carries nothing worth rendering. A 409 is "the carton
     * moved on" — sealed, shipped, or already open — and its body is an Arabic
     * sentence meant to be printed VERBATIM. The READY-only boundary is
     * deliberately not in the policy, which is exactly why a sealed carton is a
     * readable 409 rather than a bare 403.
     *
     * Past PACKED the carton is the packaging team's to open, through the
     * existing staff-only unpack. She has no door there.
     *
     * @returns {Promise<OrderBox>}
     */
  async reopen() {
    await useAxiosRepo(OrderBox).api().post(
      `/order-boxes/${this.id}/reopen`,
      {},
      { save: true },
    );

    return OrderBox.withItems(this.id);
  }

  /**
     * Close box
     *
     * @returns {Promise<OrderBox>}
     */
  async close() {
    await useAxiosRepo(OrderBox).api().post(
      `/order-boxes/${this.id}/close`,
      {},
      { save: true },
    );

    return OrderBox.find(this.id);
  }

  /**
     * Delete box permanently
     */
  async destroy() {
    await useAxiosRepo(OrderBox).api().delete(`/order-boxes/${this.id}`);

    // Manual deletion from store (Axios plugin doesn't auto-delete)
    this.$delete();
  }

  /**
   * May SHE re-open this carton? READY only — past the seal it is the
   * packaging team's, through their own unpack. Mirrors
   * `OrderBoxStatus::canBeReopened()`, which is where the backend puts the
   * boundary (not in the policy).
   */
  get canReopen() {
    return this.status === OrderBoxStatus.READY;
  }

  get statusMeta() {
    return OrderBoxStatusMeta[this.status];
  }

  /** True once the box has an estimatedTotal payload (always after creation). */
  get hasTotals() {
    return this.estimatedTotal != null;
  }

  /** True once the SHIPPED transition has minted invoice columns. */
  get hasInvoice() {
    return Boolean(this.invoiceNumber);
  }

  /** True when the box is waiting on the member to confirm delivery. */
  get needsMemberConfirmation() {
    return this.status === OrderBoxStatus.DELIVERED;
  }
}
